import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import DataTable from 'src/components/ui/data-table';
import { useMilestoneList } from 'src/services/api/queries/billingQueries';
import { useVerifyMilestone } from 'src/services/api/mutations/billingMutations';
import { useToast } from 'src/hooks/use-toast';
import { File, Building, CheckCircle, FileText, Clock } from 'lucide-react';
import BillingTabs from 'src/pages/Billing/BillingTabs/BillingTabs';
import StatsSection from 'src/components/common/StatsSection';
import { Button } from 'src/components/ui/button';
import { Alert } from 'src/components/common/Alert';
import { StatusBadge } from 'src/components/common/StatusBadge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';

// Cell components defined outside the main component
const PONumberCell = ({ value }) => (
  <div className="flex items-center">
    <File className="w-4 h-4 mr-2" />
    {value}
  </div>
);

const OrganizationCell = ({ value, row }) => {
  const customerCategory = row?.customer_category;

  if (!customerCategory) {
    return (
      <div className="flex items-center">
        <Building className="w-4 h-4 mr-2" />
        {value}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="flex items-center cursor-pointer">
            <Building className="w-4 h-4 mr-2" />
            {value}
          </div>
        </TooltipTrigger>
        <TooltipContent>{customerCategory}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const BasicCell = ({ value }) => (
  <div className="flex items-center">{value}</div>
);

const ActionsCell = ({ row, onVerify }) => {
  const { status } = row.original;

  if (status === 'PENDING') {
    return (
      <Button
        variant="outline"
        size="sm"
        className="flex items-center text-green-600 border-green-600 hover:bg-green-50 hover:text-green-700"
        onClick={() => onVerify(row.original)}
      >
        <CheckCircle className="w-4 h-4 mr-1" />
        Verify Milestone
      </Button>
    );
  }

  // For all other statuses, show 'Verified' with a green check
  return (
    <div className="flex items-center text-green-600 font-medium">
      <CheckCircle className="w-4 h-4 mr-1" /> Verified
    </div>
  );
};

const DEFAULT_PAGE_SIZE = 10;

// Define columns for DataTableV2 outside the component
const getColumns = (handleVerifyClick, handleVerifyMilestoneClick) => [
  {
    key: 'po_number',
    header: 'WO Number',
    render: (value) => <PONumberCell value={value} />,
  },
  {
    key: 'organization_name',
    header: 'Customer',
    render: (value, row) => <OrganizationCell value={value} row={row} />,
  },
  {
    key: 'requirement_title',
    header: 'Requirement Title',
    render: (value) => <BasicCell value={value} />,
  },
  {
    key: 'candidate_name',
    header: 'Candidate Name',
    render: (value) => <BasicCell value={value} />,
  },
  {
    key: 'milestone_type',
    header: 'Milestone Type',
    render: (value) => <BasicCell value={value} />,
  },
  {
    key: 'status',
    header: 'Status',
    render: (value) => <StatusBadge value={value} />,
  },
  {
    key: 'actions',
    header: 'Actions',
    render: (_value, row) => (
      <ActionsCell
        row={{ original: row }}
        onVerify={handleVerifyClick}
        onVerifyMilestone={handleVerifyMilestoneClick}
      />
    ),
  },
];

function ListMilestones() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive pagination from URL params (Opportunities pattern)
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const { data: milestoneResponse, isFetching } = useMilestoneList({
    page: currentPage,
    pageSize: currentPageSize,
  });
  // Invalidates ['milestones'] on success, so the list refetches automatically.
  const verifyMilestoneMutation = useVerifyMilestone();

  const milestones =
    milestoneResponse?.data?.items || milestoneResponse?.items || [];
  const pagination =
    milestoneResponse?.data?.pagination || milestoneResponse?.pagination || {};
  const totalItems = pagination.total ?? 0;
  const totalPages =
    pagination.pages ||
    Math.ceil((totalItems || 0) / (currentPageSize || DEFAULT_PAGE_SIZE)) ||
    1;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [selectedProcessedMilestone, setSelectedProcessedMilestone] =
    useState(null);
  const isVerifying = verifyMilestoneMutation.isPending;
  const isVerifyingMilestone = verifyMilestoneMutation.isPending;

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handleVerifyClick = (milestone) => {
    setSelectedMilestone(milestone);
    setDialogOpen(true);
  };

  const handleVerifyMilestoneClick = (milestone) => {
    setSelectedProcessedMilestone(milestone);
    setMilestoneDialogOpen(true);
  };

  const handleVerifyConfirm = () => {
    if (!selectedMilestone) return;

    verifyMilestoneMutation.mutate(selectedMilestone.id, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: `Milestone ${selectedMilestone.po_number} has been verified successfully.`,
          variant: 'default',
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description:
            error.message || 'Failed to verify milestone. Please try again.',
          variant: 'destructive',
        });
      },
      onSettled: () => {
        setDialogOpen(false);
        setSelectedMilestone(null);
      },
    });
  };

  const handleVerifyCancel = () => {
    setDialogOpen(false);
    setSelectedMilestone(null);
  };

  const handleVerifyMilestoneConfirm = () => {
    if (!selectedProcessedMilestone) return;

    verifyMilestoneMutation.mutate(selectedProcessedMilestone.id, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: `Milestone ${selectedProcessedMilestone.po_number} has been verified successfully.`,
          variant: 'default',
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description:
            error.message || 'Failed to verify milestone. Please try again.',
          variant: 'destructive',
        });
      },
      onSettled: () => {
        setMilestoneDialogOpen(false);
        setSelectedProcessedMilestone(null);
      },
    });
  };

  const handleVerifyMilestoneCancel = () => {
    setMilestoneDialogOpen(false);
    setSelectedProcessedMilestone(null);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get columns with access to handleVerifyClick and handleVerifyMilestoneClick
  const columns = getColumns(handleVerifyClick, handleVerifyMilestoneClick);

  const pendingCount = milestones.filter((m) => m.status === 'PENDING').length;
  const verifiedCount = milestones.filter((m) => m.status !== 'PENDING').length;

  return (
    <div>
      <PageHeader title="Milestones" />

      <PageContainer>
        <StatsSection
          title="Summary"
          className="mb-5"
          cards={[
            {
              key: 'total',
              label: 'Total Milestones',
              value: totalItems || 0,
              iconColor: 'var(--primary)',
              icon: (
                <FileText style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'pending',
              label: 'Pending',
              value: pendingCount,
              iconColor: '#B45309',
              icon: (
                <Clock style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'verified',
              label: 'Verified',
              value: verifiedCount,
              iconColor: '#0D9668',
              icon: (
                <CheckCircle
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
          ]}
        />

        <BillingTabs />

        <DataTable
          data={milestones || []}
          columns={columns}
          loading={isFetching}
          rowKey="id"
          hover
          stickyHeader
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
          emptyState={
            <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
              <div
                className="flex items-center justify-center mb-5 rounded-full bg-border"
                style={{ width: 72, height: 72 }}
              >
                <FileText
                  className="text-muted-foreground"
                  style={{ width: 32, height: 32, strokeWidth: 1.5 }}
                />
              </div>
              <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
                No milestones found
              </p>
              <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
                Milestones will appear here once they are available.
              </p>
            </div>
          }
        />
      </PageContainer>

      {/* Verification Confirmation Dialog */}
      <Alert
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Verify Milestone"
        description="By clicking 'Confirm Verification', you are certifying that this information is correct, and you understand that this will trigger invoice generation for the appropriate milestone payment."
        cancelText="Cancel"
        continueText={isVerifying ? 'Verifying...' : 'Confirm Verfication'}
        onCancel={handleVerifyCancel}
        onContinue={handleVerifyConfirm}
      />

      {/* Milestone Verification Confirmation Dialog */}
      <Alert
        isOpen={milestoneDialogOpen}
        onClose={() => setMilestoneDialogOpen(false)}
        title="Verify Milestone"
        description="By clicking 'Confirm Milestone Verification', you are certifying that the milestone details are correct and ready for processing."
        cancelText="Cancel"
        continueText={
          isVerifyingMilestone
            ? 'Verifying...'
            : 'Confirm Milestone Verification'
        }
        onCancel={handleVerifyMilestoneCancel}
        onContinue={handleVerifyMilestoneConfirm}
      />
    </div>
  );
}

export default ListMilestones;
